import { useMemo } from 'react'

import {
  type LegacyFilter,
  type RawInlineFilter,
  useListFilters,
  useInlineFilters,
} from '@tribeplatform/react-components/Filters'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'

import { useCustomFields } from '../hooks/useCustomFields.js'
import { convertToPostListFilter } from '../utils/convertToPostListFilter.js'
import { staticFiltersFactory } from '../utils/staticFilters.js'

interface UsePostFiltersProps {
  filterBy: LegacyFilter[]
  postTypeSlugs: string[]
  rawInlineFilters: RawInlineFilter[]
}
export const usePostFilters = ({
  filterBy,
  postTypeSlugs,
  rawInlineFilters,
}: UsePostFiltersProps) => {
  const { customFields } = useCustomFields({ postTypeSlugs })
  const staticFilters = useStaticIntl(staticFiltersFactory)
  const ListFilters = useMemo(
    () => convertToPostListFilter(filterBy),
    [filterBy],
  )
  const { filters, allFilters, prepareFilters } = useListFilters({
    entityListFilters: ListFilters,
    customFields,
    staticFilters,
  })
  const {
    processedInlineFilters,
    setProcessedInlineFilter,
    setProcessedInlineFilters,
    resetProcessedInlineFilter,
  } = useInlineFilters({
    entityListFilters: ListFilters,
    filters,
    allFilters,
    rawInlineFilters,
  })
  const combinedFilters = useMemo(
    () => prepareFilters([...filters, ...processedInlineFilters]),
    [filters, prepareFilters, processedInlineFilters],
  )
  const hasInlineFilters = rawInlineFilters.some(({ enabled }) => !!enabled)

  return {
    processedInlineFilters,
    setProcessedInlineFilter,
    setProcessedInlineFilters,
    resetProcessedInlineFilter,
    combinedFilters,
    hasInlineFilters,
  }
}
